import { Html } from '@react-three/drei';
import { LoadingAnimation, MantineOverrideProvider } from '@uag/react-core';
import { useMemo } from 'react';
import { Vector3 } from 'three';
import { useShallow } from 'zustand/shallow';

import { calculateFullQuaternion } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import {
    EMPTY_PANEL_REPLACEMENT_GRID,
    PANEL_REPLACEMENT_OVERLAY_Z_INDEX_RANGE,
    getCachedPanelReplacementGrid,
    getSelectedPanel
} from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/utils/quickPanelReplacementUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { QuickPanelReplacementPreviewProps } from 'app/modules/wallforming/components/scenes/tools/quick-panel-replacement/types/quickPanelReplacementTypes';

const systemId = import.meta.env.VITE_DEFAULT_SYSTEM_ID;
const center = new Vector3();

export const QuickPanelReplacementPreview = ({ articleId }: QuickPanelReplacementPreviewProps) => {
    const filterActive = usePlacementStore((state) => state.isPPMFilterActive);
    const grid = getCachedPanelReplacementGrid(systemId, filterActive) ?? EMPTY_PANEL_REPLACEMENT_GRID;

    const selected = usePlacementStore(
        useShallow((state) => {
            if (!state.isPanelQuickReplacementOpen) {
                return null;
            }
            const panel = getSelectedPanel(state, grid);
            return panel?.articleId === articleId ? panel : null;
        })
    );

    const part = useMemo(() => selected?.instance.parts[Object.keys(selected.instance.parts)[0]], [selected]);
    const quaternion = useMemo(() => part && calculateFullQuaternion(part.alignment, part.rotation), [part]);
    const bounds = useMemo(() => selected?.item.bounds, [selected]);

    const position = useMemo(() => {
        if (!selected || !part || !bounds) {
            return null;
        }
        center.set(
            (bounds.x.min + bounds.x.max) / 2,
            (bounds.y.min + bounds.y.max) / 2,
            (bounds.z.min + bounds.z.max) / 2
        );
        const quaternion = calculateFullQuaternion(part.alignment, part.rotation);
        return center.multiply(part.scale).applyQuaternion(quaternion).add(part.position);
    }, [part, selected, bounds]);

    if (!selected || !part || !bounds || !position) {
        return null;
    }

    return (
        <>
            <mesh position={position} quaternion={quaternion} scale={part.scale}>
                <boxGeometry
                    args={[bounds.x.max - bounds.x.min, bounds.y.max - bounds.y.min, bounds.z.max - bounds.z.min]}
                />
                <meshBasicMaterial color="#2f80ed" depthWrite={false} opacity={0.22} transparent />
            </mesh>
            <Html
                key={articleId}
                distanceFactor={0.0019}
                occlude={false}
                position={position}
                zIndexRange={PANEL_REPLACEMENT_OVERLAY_Z_INDEX_RANGE}
                center
            >
                <MantineOverrideProvider>
                    <LoadingAnimation />
                </MantineOverrideProvider>
            </Html>
        </>
    );
};
